interface MaterialEntry {
  uuid?: string;
  isAlias?: boolean;
  aliasForProductUuid?: string;
  materialType?: string;
  productionType?: string;
  name?: string;
  [key: string]: unknown;
}

export type CampaignStatus = 'OPEN' | 'ACTIVE' | 'COMPLETED';

interface ProducedQuantityInput {
  materialId: string;
  istQuantity?: number;
  oldSollQuantity?: number;
  isLocked?: boolean;
  materialName?: string;
  campaignStatus?: CampaignStatus;
  startedDoughBatchCount?: number;
  totalDoughBatchCount?: number;
}

interface PendingOrderInput {
  materialId?: string;
  demandDate?: string;
  quantity?: number;
  uuid?: string;
}

export interface ReplanningGridRow {
  materialId: string;
  materialName: string;
  actualQuantity: number;
  oldTargetQuantity: number;
  newTargetQuantity: number;
  deviationQuantity: number;
  isChanged: boolean;
  newTargetUuid: string | undefined;
  isLocked: boolean;
  isNewTargetBelowActual: boolean;
  /** Locked AND actual > new target: the user cannot change anything, does not block 'Next'. */
  isLockedAndExceeded: boolean;
  /** Aggregated campaign state of the material at the end of the frozen zone. */
  campaignStatus: CampaignStatus;
  /** Number of dough batches already started (total over all campaigns of the material). */
  startedDoughBatchCount: number;
  /** Total number of dough batches over all campaigns of the material (for the 'X/Y' display). */
  totalDoughBatchCount: number;
  /** Locked + actual > old target: the actual quantity exceeds what was originally planned. */
  isOvermenge: boolean;
  overmengeQuantity: number;
}

/**
 * Filters the products that are relevant for the replanning grid.
 * Intermediate products (INTERMEDIATE_PRODUCT) stay out: replanning only concerns
 * finished-product campaigns; intermediate products are pegged/scheduled separately afterwards.
 * Alias products are visible - every product carries its own demands,
 * the aggregation (alias -> main product) happens only during batch determination.
 */
export function filterReplanningProductMaterials(materials: MaterialEntry[]): MaterialEntry[] {
  return materials.filter(
    (m) =>
      m.materialType === 'PRODUCT' ||
      (m.productionType === 'MAKE' && m.materialType !== 'INTERMEDIATE_PRODUCT'),
  );
}

/**
 * Builds a map with actual / old-target data per materialId.
 * No alias resolution - every product shows its own campaign data.
 * Several rows for the same UUID (several campaigns) are summed up.
 */
interface ByProducedEntry {
  actualQuantity: number;
  oldTargetQuantity: number;
  materialName: string;
  isLocked: boolean;
  campaignStatus: CampaignStatus;
  startedDoughBatchCount: number;
  totalDoughBatchCount: number;
}

/** Aggregation rule for the status when a material has several entries (edge case). */
function mergeStatus(a: CampaignStatus, b: CampaignStatus): CampaignStatus {
  if (a === 'ACTIVE' || b === 'ACTIVE') return 'ACTIVE';
  if (a === 'OPEN' || b === 'OPEN') return a === b ? 'OPEN' : 'ACTIVE';
  return 'COMPLETED';
}

export function buildByProduced(
  producedQuantitiesRows: ProducedQuantityInput[],
): Map<string, ByProducedEntry> {
  const byProduced = new Map<string, ByProducedEntry>();
  producedQuantitiesRows.forEach((row) => {
    const status: CampaignStatus = row.campaignStatus ?? 'OPEN';
    const startedBatches = row.startedDoughBatchCount ?? 0;
    const totalBatches = row.totalDoughBatchCount ?? 0;
    const existing = byProduced.get(row.materialId);
    if (existing) {
      existing.actualQuantity += row.istQuantity ?? 0;
      existing.oldTargetQuantity += row.oldSollQuantity ?? 0;
      existing.isLocked = existing.isLocked && (row.isLocked ?? false);
      existing.campaignStatus = mergeStatus(existing.campaignStatus, status);
      existing.startedDoughBatchCount += startedBatches;
      existing.totalDoughBatchCount += totalBatches;
    } else {
      byProduced.set(row.materialId, {
        actualQuantity: row.istQuantity ?? 0,
        oldTargetQuantity: row.oldSollQuantity ?? 0,
        materialName: row.materialName ?? '',
        isLocked: row.isLocked ?? false,
        campaignStatus: status,
        startedDoughBatchCount: startedBatches,
        totalDoughBatchCount: totalBatches,
      });
    }
  });
  return byProduced;
}

export interface BuildReplanningGridRowsParams {
  replanningProductMaterials: MaterialEntry[];
  producedQuantitiesRows: ProducedQuantityInput[];
  /** Pending orders - for the UUID lookup and as a quantity fallback when newTargetByMaterial is empty. */
  pendingOrders: PendingOrderInput[];
  /** ISO date (YYYY-MM-DD) of the canonical demand date (plannedEnd day of the source schedule). */
  canonicalDateStr: string;
  /**
   * New target values per materialId on the canonical date.
   * Key = the actual materialId (an alias stays the alias UUID, it is not aggregated).
   * Source: orders from the wizard context (contains DB demands + pending orders).
   * Can be empty before frozenZoneEnd is set -> falls back to pendingOrders.
   */
  newTargetByMaterial: Map<string, number>;
}

/**
 * Builds the grid rows for the replanning grid.
 * Every product (alias as well as main) gets its own row with its own
 * actual / old-target values; the new target comes from the demand records per materialId.
 * The alias aggregation happens only in startPlanningWithDefaults (batch determination).
 */
export function buildReplanningGridRows(params: BuildReplanningGridRowsParams): ReplanningGridRow[] {
  const {
    replanningProductMaterials,
    producedQuantitiesRows,
    pendingOrders,
    canonicalDateStr,
    newTargetByMaterial,
  } = params;

  const byProduced = buildByProduced(producedQuantitiesRows);

  const key = (mId: string, d: string) => `${mId}|${d}`;

  // UUID lookup for update vs. create on save
  const pendingByKey = new Map<string, string>();
  // Quantity lookup straight from pendingOrders per materialId (alias UUID as the key, not aggregated)
  const pendingQuantityByMaterialId = new Map<string, number>();
  pendingOrders.forEach((d) => {
    const dStr = d.demandDate?.split('T')[0] || '';
    if (!d.materialId || !dStr) return;
    pendingByKey.set(key(d.materialId, dStr), d.uuid ?? '');
    if (dStr === canonicalDateStr) {
      pendingQuantityByMaterialId.set(
        d.materialId,
        (pendingQuantityByMaterialId.get(d.materialId) ?? 0) + (d.quantity ?? 0),
      );
    }
  });

  return replanningProductMaterials.map((mat) => {
    const uuid = mat.uuid ?? '';
    const produced = byProduced.get(uuid);
    const actualQuantity = produced?.actualQuantity ?? 0;
    const oldTargetQuantity = produced?.oldTargetQuantity ?? 0;
    const isLocked = produced?.isLocked ?? false;
    const materialName = produced?.materialName ?? mat.name ?? '';
    const campaignStatus: CampaignStatus = produced?.campaignStatus ?? 'OPEN';
    const startedDoughBatchCount = produced?.startedDoughBatchCount ?? 0;
    const totalDoughBatchCount = produced?.totalDoughBatchCount ?? 0;
    // Preferred: newTargetByMaterial (orders including pending ones, when plannedStart is set);
    // fallback: pendingQuantityByMaterialId (straight from pendingOrders, before frozenZoneEnd is chosen).
    const explicitNewTarget = newTargetByMaterial.has(uuid)
      ? (newTargetByMaterial.get(uuid) ?? 0)
      : (pendingQuantityByMaterialId.get(uuid) ?? 0);
    const hasExplicitTarget = newTargetByMaterial.has(uuid) || pendingQuantityByMaterialId.has(uuid);
    // When locked: show the planned value (old target) - the user has no choice left here,
    // but should understand 'we stay with the old plan'. Deviation = 0. If actual > old target
    // (more was actually produced than planned), that becomes visible through the excess badge.
    const newTargetQuantity = isLocked ? oldTargetQuantity : explicitNewTarget;
    const deviationQuantity = newTargetQuantity - oldTargetQuantity;
    const newTargetUuid = canonicalDateStr ? pendingByKey.get(key(uuid, canonicalDateStr)) : undefined;
    // Only set isNewTargetBelowActual when an explicit demand exists -
    // no demand (= default value 0) must not block 'Next'.
    const isNewTargetBelowActual = hasExplicitTarget && (explicitNewTarget < actualQuantity);
    // Excess: locked + actually produced more than originally planned.
    const overmengeQuantity = isLocked && actualQuantity > oldTargetQuantity ? actualQuantity - oldTargetQuantity : 0;
    return {
      materialId: uuid,
      materialName,
      actualQuantity,
      oldTargetQuantity,
      newTargetQuantity,
      deviationQuantity,
      isChanged: newTargetQuantity !== oldTargetQuantity,
      newTargetUuid,
      isLocked,
      isNewTargetBelowActual,
      isLockedAndExceeded: isLocked && isNewTargetBelowActual,
      campaignStatus,
      startedDoughBatchCount,
      totalDoughBatchCount,
      isOvermenge: overmengeQuantity > 0,
      overmengeQuantity,
    };
  });
}
